/*eslint import/namespace: ['error', { allowComputed: true }]*/
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import HttpApi from 'i18next-http-backend';
import axios from 'axios';
import * as locales from 'date-fns/locale';
import { Configuration } from './Interfaces';
import { unflatten } from 'flat';
import RuntimeConfigurationProvider from './RuntimeConfigurationProvider';

declare module 'react-i18next' {
  type ResourceObject = Record<string, any>;

  interface CustomTypeOptions {
    defaultNS: 'common';
    resources: ResourceObject;
  }
}

const UpdateLocalStorageWithLatestMetadata = (configuration: Configuration) => {
  axios
    .get(`${configuration.LocalesBaseUrl}/locales-metadata.json`)
    .then((res) => {
      if (res.data) {
        localStorage.setItem('locales-metadata', JSON.stringify(res.data));
      }
    });
};

const DetermineLocalesVersionBasedOnMetadaAndConfig = (
  configuration: Configuration
) => {
  const localesJson = localStorage.getItem('locales-metadata');
  if (localesJson) {
    const metadataObject = JSON.parse(localesJson);
    const [metadataDate, metadataIncrement, metadataBuildNumber] =
      metadataObject.version.split('.');
    const [confDate, confIncrement, configBuildNumber] = (
      configuration.LocalesVersion || '0.0.0'
    ).split('.');

    const metadataVersionPrefix =
      Number(metadataDate) * 1000 + Number(metadataIncrement);
    const confVersionPrefix = Number(confDate) * 1000 + Number(confIncrement);

    if (metadataVersionPrefix > confVersionPrefix) {
      return metadataObject.version;
    }
    if (metadataVersionPrefix === confVersionPrefix) {
      return Number(metadataBuildNumber) > Number(configBuildNumber)
        ? metadataObject.version
        : configuration.LocalesVersion;
    }
    return configuration.LocalesVersion;
  }
  return configuration.LocalesVersion;
};

const getCurrentLocalesVersionAndUpdateLocalStorage = (
  configuration: Configuration
) => {
  UpdateLocalStorageWithLatestMetadata(configuration);
  return DetermineLocalesVersionBasedOnMetadaAndConfig(configuration);
};

const getLocalizationFilesPath = (
  lng: string[],
  ns: string[],
  configuration: Configuration
) => {
  // THIS IS NEW CONTENT DELIVERY API
  if (ns.length === 1 && ns[0].startsWith('cd/')) {
    const namespace = ns[0].replace('cd/', '');
    return `/umbraco/delivery/api/v2/content/item/${namespace}`;
  }

  const base = configuration.LocalesBaseUrl;
  const version = configuration.LocalesVersion
    ? `/${getCurrentLocalesVersionAndUpdateLocalStorage(configuration)}`
    : '';

  const path = `${base}${version}/{{lng}}/{{ns}}.json`;
  return path;
};

export const getI18nOptions = () => ({
  lng: 'en',
  fallbackLng: 'en',
  interpolation: { escapeValue: false },
  defaultNS: 'common',
  returnObjects: true,
  ns: 'common',
  preload: ['en'],
});

const initLocalization = (configuration: Configuration) => {
  i18n
    .use(HttpApi)
    .use(initReactI18next)
    .init({
      ...getI18nOptions(),
      debug: configuration.UseLocalizationDebug,
      backend: {
        loadPath: (lng: string[], ns: string[]) =>
          getLocalizationFilesPath(lng, ns, configuration),
        parse: (data: any) => {
          const json = JSON.parse(data);
          if (json.contentType && json.properties) {
            return unflatten(json.properties, { delimiter: '__' });
          }

          return json;
        },
      },
    });
};

export const decorateWithLocalMediaBaseUrl = (url: string) => {
  const configuration = new RuntimeConfigurationProvider().getConfiguration();
  return url.replace('$(LOCAL_MEDIA_URL)', configuration.MediaBaseUrl || '');
};

export const decorateWithDesignSystemMediaBaseUrl = (url: string) => {
  const configuration = new RuntimeConfigurationProvider().getConfiguration();
  return url.replace(
    '$(DESIGN_SYSTEM_MEDIA_URL)',
    configuration.DesignSystemMediaBaseUrl || ''
  );
};

export const userLocale: string =
  navigator.languages && navigator.languages.length
    ? navigator.languages[0]
    : // @ts-ignore // doesn't recognise IE navigator fields
      navigator.userLanguage ||
      navigator.language ||
      // @ts-ignore // doesn't recognise IE navigator fields
      navigator.browserLanguage ||
      'en-US';

export const userLocaleObject =
  // @ts-ignore // dynamic locales loader
  locales[userLocale.replace('-', '')] ||
  // @ts-ignore // dynamic locales loader
  locales[userLocale.substring(0, 2)] ||
  locales.enUS;

export default initLocalization;
